/**
 * Coin Detail Screen - candle chart (1m/5m/1h) + market buy/sell
 * Sub-screen of the exchange tab. Profit = red, loss = blue.
 */
import { COINS, type Timeframe } from '../game/config/coins.js';
import { getChange24h } from '../game/state.js';
import { getPnlPercent, getUnrealizedPnl } from '../game/systems/tradingSystem.js';
import { formatNumber, formatPrice } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { coinIconHtml, coinDisplayName } from './coinIcon.js';
import { onRender, getState, getViewState, setViewState, dispatch, showToast, markScreenDirty, requestRender } from './renderer.js';
import { drawCandleChart } from './candleChart.js';
import { renderCoinActions } from './coinActionsPanel.js';
import { offerInsurance } from './riskHooksPanel.js';
import { tradeJuice } from './juice.js';

type TradeMode = 'buy' | 'sell';

const TF_LABELS: { tf: Timeframe; label: string }[] = [
  { tf: 'm1', label: '1m' },
  { tf: 'm5', label: '5m' },
  { tf: 'h1', label: '1h' },
];
const PRESETS = [10, 25, 50, 100];

let container: HTMLDivElement | null = null;
let canvas: HTMLCanvasElement | null = null;
let selectedTf: Timeframe = 'm1';
let tradeMode: TradeMode = 'buy';
let selectedPercent = 25;

export function initCoinDetailScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'coindetail-screen';
  container.className = 'screen-panel exchange-theme';
  buildSkeleton();
  content.appendChild(container);

  onRender(renderCoinDetail, { alwaysTabs: ['coindetail'] });
}

function buildSkeleton() {
  if (!container) return;
  const tfButtons = TF_LABELS
    .map(x => `<button class="ex-tf-btn" data-tf="${x.tf}">${x.label}</button>`)
    .join('');
  const presetButtons = PRESETS
    .map(p => `<button class="ex-preset-btn" data-pct="${p}">${p === 100 ? t('ex_max') : p + '%'}</button>`)
    .join('');

  container.innerHTML = `
    <div class="ex-detail-head">
      <button class="ex-back-btn" data-action="back">&lt; ${t('ex_back')}</button>
      <div class="ex-detail-title" id="cd-title"></div>
    </div>
    <div class="ex-detail-price" id="cd-price"></div>
    <div class="ex-tf-row" id="cd-tf-row">${tfButtons}</div>
    <div class="ex-chart-wrap"><canvas id="cd-canvas" class="ex-candle-canvas"></canvas></div>
    <div class="ex-holding-box" id="cd-holding"></div>
    <div class="ex-trade-box">
      <div class="ex-trade-tabs" id="cd-trade-tabs">
        <button class="ex-trade-tab" data-mode="buy">${t('ex_buy')}</button>
        <button class="ex-trade-tab" data-mode="sell">${t('ex_sell')}</button>
      </div>
      <div class="ex-preset-row" id="cd-preset-row">${presetButtons}</div>
      <div class="ex-trade-info" id="cd-trade-info"></div>
      <button class="ex-trade-btn" id="cd-trade-btn"></button>
    </div>
    <div class="cd-actions" id="cd-actions"></div>`;

  canvas = container.querySelector('#cd-canvas');
  bindEvents();
}

function bindEvents() {
  if (!container) return;
  container.querySelector('[data-action="back"]')?.addEventListener('click', () => {
    playSfx('tap');
    setViewState({ currentTab: 'exchange', selectedCoinId: null });
  });
  container.querySelectorAll('[data-tf]').forEach(btn => {
    btn.addEventListener('click', () => {
      selectedTf = (btn as HTMLElement).dataset.tf as Timeframe;
      playSfx('tap');
      requestRender();
    });
  });
  container.querySelectorAll('[data-mode]').forEach(btn => {
    btn.addEventListener('click', () => {
      tradeMode = (btn as HTMLElement).dataset.mode as TradeMode;
      playSfx('tap');
      requestRender();
    });
  });
  container.querySelectorAll('[data-pct]').forEach(btn => {
    btn.addEventListener('click', () => {
      selectedPercent = Number((btn as HTMLElement).dataset.pct);
      playSfx('tap');
      requestRender();
    });
  });
  container.querySelector('#cd-trade-btn')?.addEventListener('click', executeTrade);
}

function getTradePreview(coinId: string): { spend: number; amount: number } {
  const st = getState();
  const coin = st.exchange.coins[coinId];
  const holding = st.exchange.holdings[coinId];
  if (!coin) return { spend: 0, amount: 0 };

  if (tradeMode === 'buy') {
    const spend = st.coins * (selectedPercent / 100);
    return { spend, amount: coin.price > 0 ? spend / coin.price : 0 };
  }
  const amount = (holding?.amount ?? 0) * (selectedPercent / 100);
  return { spend: amount * coin.price, amount };
}

function executeTrade() {
  const coinId = getViewState().selectedCoinId;
  if (!coinId) return;
  const { spend, amount } = getTradePreview(coinId);

  if (tradeMode === 'buy') {
    if (spend <= 0) { playSfx('error'); showToast(t('ex_insufficient_funds')); return; }
    const tradesBefore = getState().exchange.totalTrades;
    dispatch({ type: 'EXCHANGE_BUY', payload: { coinId, spend } });
    playSfx('purchase');
    showToast(t('ex_buy_success'));
    // Rug insurance offer banner right after a filled meme-coin buy (10s)
    if (getState().exchange.totalTrades > tradesBefore) offerInsurance(coinId);
  } else {
    if (amount <= 0) { playSfx('error'); showToast(t('ex_insufficient_holding')); return; }
    // realized P/L on the sold slice (computed before the holding mutates)
    const stNow = getState();
    const holdingNow = stNow.exchange.holdings[coinId];
    const coinNow = stNow.exchange.coins[coinId];
    let pnlPct = 0, pnlAmount = 0;
    if (holdingNow && coinNow) {
      pnlPct = getPnlPercent(holdingNow, coinNow.price);
      pnlAmount = getUnrealizedPnl({ amount, avgPrice: holdingNow.avgPrice }, coinNow.price);
    }
    dispatch({ type: 'EXCHANGE_SELL', payload: { coinId, amount } });
    playSfx('purchase');
    showToast(t('ex_sell_success'));
    tradeJuice(pnlPct, pnlAmount);
  }
  markScreenDirty();
  requestRender();
}

function pnlClass(value: number): string {
  if (value > 0) return 'ex-profit';
  if (value < 0) return 'ex-loss';
  return 'ex-flat';
}

function signed(value: number, decimals = 2): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}`;
}

function setText(id: string, text: string) {
  const el = container?.querySelector(`#${id}`);
  if (el && el.textContent !== text) el.textContent = text;
}

function updateHeader(coinId: string) {
  const st = getState();
  const cfg = COINS.find(c => c.id === coinId);
  const coin = st.exchange.coins[coinId];
  if (!cfg || !coin) return;

  const listing = st.exchange.listing[coinId];
  const titleEl = container?.querySelector('#cd-title');
  if (titleEl) {
    const html = `${coinIconHtml(cfg, true, listing)} ${coinDisplayName(cfg, listing)} <span class="ex-coin-symbol-inline">${cfg.symbol}</span>`;
    if (titleEl.innerHTML !== html) titleEl.innerHTML = html;
  }

  const change = getChange24h(coin);
  const priceEl = container?.querySelector('#cd-price');
  if (priceEl) {
    priceEl.innerHTML = `<span class="ex-detail-price-main">${formatPrice(coin.price)}</span>
      <span class="ex-coin-change ${pnlClass(change)}">${signed(change)}% (24h)</span>`;
  }
}

function updateHoldingBox(coinId: string) {
  const st = getState();
  const coin = st.exchange.coins[coinId];
  const holding = st.exchange.holdings[coinId];
  const box = container?.querySelector('#cd-holding');
  if (!box || !coin) return;

  if (!holding) {
    const html = `<div class="ex-holding-empty">${t('ex_no_holding')}</div>`;
    if (box.innerHTML !== html) box.innerHTML = html;
    return;
  }
  const pnl = getPnlPercent(holding, coin.price);
  box.innerHTML = `<div class="ex-holding-grid">
    <div><span class="ex-label">${t('ex_amount')}</span><span>${formatNumber(holding.amount)}</span></div>
    <div><span class="ex-label">${t('ex_avg_price')}</span><span>${formatPrice(holding.avgPrice)}</span></div>
    <div><span class="ex-label">${t('ex_value')}</span><span>${formatNumber(holding.amount * coin.price)}</span></div>
    <div><span class="ex-label">${t('ex_pnl')}</span><span class="${pnlClass(pnl)}">${signed(pnl)}%</span></div>
  </div>`;
}

function updateTradeBox(coinId: string) {
  container?.querySelectorAll('[data-mode]').forEach(btn => {
    btn.classList.toggle('active', (btn as HTMLElement).dataset.mode === tradeMode);
    btn.classList.toggle('ex-tab-sell', (btn as HTMLElement).dataset.mode === 'sell');
  });
  container?.querySelectorAll('[data-pct]').forEach(btn => {
    btn.classList.toggle('active', Number((btn as HTMLElement).dataset.pct) === selectedPercent);
  });

  const { spend, amount } = getTradePreview(coinId);
  const info = tradeMode === 'buy'
    ? `${t('ex_spend')}: ${formatNumber(spend)} / ${t('ex_amount')}: ${formatNumber(amount)}`
    : `${t('ex_sell_amount')}: ${formatNumber(amount)} / ${t('ex_value')}: ${formatNumber(spend)}`;
  setText('cd-trade-info', info);

  const tradeBtn = container?.querySelector('#cd-trade-btn');
  if (tradeBtn) {
    tradeBtn.textContent = tradeMode === 'buy' ? t('ex_buy') : t('ex_sell');
    tradeBtn.classList.toggle('ex-btn-buy', tradeMode === 'buy');
    tradeBtn.classList.toggle('ex-btn-sell', tradeMode === 'sell');
  }
}

function renderCoinDetail() {
  if (!container) return;
  const coinId = getViewState().selectedCoinId;
  if (!coinId) return;
  const st = getState();
  const coin = st.exchange.coins[coinId];
  if (!coin) return;
  // Bounce back if the coin was delisted while its detail was open.
  if (st.exchange.listing[coinId]?.status !== 'listed' && !st.exchange.holdings[coinId]) {
    setViewState({ currentTab: 'exchange', selectedCoinId: null });
    return;
  }

  updateHeader(coinId);

  container.querySelectorAll('[data-tf]').forEach(btn => {
    btn.classList.toggle('active', (btn as HTMLElement).dataset.tf === selectedTf);
  });
  if (canvas) drawCandleChart(canvas, coin.candles[selectedTf]);

  updateHoldingBox(coinId);
  updateTradeBox(coinId);

  const actionsEl = container.querySelector('#cd-actions');
  if (actionsEl) renderCoinActions(actionsEl as HTMLElement, coinId);
}
